import React from "react";
import {
  Sparkle,
  ArrowsHorizontal,
  ArrowsClockwise,
  ShieldCheck,
} from "@phosphor-icons/react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import RegenerateButton from "@/components/RegenerateButton";

/**
 * ResultViewer — viewer for the single-photo "done" state.
 * Shows: pill with applied focuses + tap & hold preview (touch + mouse) to
 * see the original quickly + the existing BeforeAfterSlider below for users
 * who prefer the slider.
 *
 * iter v28 r358 — User M1267: «in ogni foto modificata aprire un popup
 * "Non ti piace? Rigenera"». Aggiunto bottone esplicito "Non ti piace?
 * Rigenera" che ri-esegue con gli STESSI parametri (output è stocastico:
 * ogni esecuzione produce un risultato leggermente diverso). Distinto dal
 * vecchio "Riprova" (RegenerateButton) che invece torna ai controlli per
 * cambiare modalità/focus. Per la modalità enhance aggiunto anche un
 * toggle "Ultra-fedele" che ri-esegue con focus="fedele" (zero invenzioni).
 *
 * iter v28 r372 — estratto da FotoEditor.jsx (era cresciuto a 2267 righe).
 * Nessuna modifica di logica.
 */
export function ResultViewer({
  item, afterUrl, focusLabels, modeLabel,
  onRegenerate, onRigenera, onUltraFedele,
  showUltraFedele, ultraFedele,
  disabled,
}) {
  const [holding, setHolding] = React.useState(false);
  const holdHandlers = {
    onMouseDown:  () => setHolding(true),
    onMouseUp:    () => setHolding(false),
    onMouseLeave: () => setHolding(false),
    onTouchStart: (e) => { e.preventDefault(); setHolding(true); },
    onTouchEnd:   () => setHolding(false),
    onTouchCancel:() => setHolding(false),
  };

  return (
    <div className="space-y-3" data-testid="foto-result">
      {/* Pillola "Migliorato: ..." */}
      {(focusLabels?.length || modeLabel) && (
        <div className="flex items-center flex-wrap gap-1.5 px-1" data-testid="foto-result-pills">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] bg-[#A8B89A]/30 px-2 py-1 rounded-full">
            <Sparkle size={10} weight="fill" /> Migliorato
          </span>
          {modeLabel ? (
            <span className="text-[11.5px] font-medium text-[#3F3D38]">{modeLabel}</span>
          ) : (
            focusLabels.map((l) => (
              <span key={l} className="text-[11.5px] font-medium text-[#3F3D38] bg-white border border-[#E5E7E0] px-2 py-0.5 rounded-full">
                {l}
              </span>
            ))
          )}
        </div>
      )}

      {/* Tap & hold quick comparator — full-bleed image with overlay button */}
      <div
        className="relative rounded-2xl overflow-hidden bg-[#1C1C1A] h-[42vh] sm:h-[48vh] select-none"
        data-testid="foto-result-quickview"
      >
        <img
          src={holding ? item.previewUrl : afterUrl}
          alt={holding ? "Prima" : "Dopo"}
          className="w-full h-full object-contain transition-opacity"
          draggable={false}
        />
        {/* Pillola in alto a sinistra: stato corrente */}
        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full backdrop-blur-sm ${
          holding ? "bg-white/85 text-[#3F3D38]" : "bg-[#7C8B6E]/85 text-white"
        }`}>
          {holding ? "Prima" : "Dopo"}
        </span>
        {/* Hold-to-preview button */}
        <button
          {...holdHandlers}
          type="button"
          className={`absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold backdrop-blur-md transition-all ${
            holding
              ? "bg-white text-[#1C1C1A] shadow-lg scale-105"
              : "bg-[#1C1C1A]/80 text-white hover:bg-[#1C1C1A]/95"
          }`}
          data-testid="foto-result-hold-btn"
          aria-label="Tieni premuto per vedere il prima"
        >
          <ArrowsHorizontal size={13} weight="bold" />
          {holding ? "Stai vedendo il PRIMA" : "Tieni premuto per il prima"}
        </button>
      </div>

      {/* Slider sotto — per chi preferisce il confronto con tendina */}
      <details className="group rounded-xl border border-[#EAE7DF] bg-white" data-testid="foto-result-slider-details">
        <summary className="cursor-pointer list-none px-3.5 py-2.5 text-[12px] font-medium text-[#3F3D38] inline-flex items-center justify-between w-full select-none">
          <span className="inline-flex items-center gap-1.5"><ArrowsHorizontal size={13} weight="bold" /> Slider Prima/Dopo</span>
          <span className="text-[10px] text-[#9A968B] group-open:hidden">apri</span>
          <span className="text-[10px] text-[#9A968B] hidden group-open:inline">chiudi</span>
        </summary>
        <div className="px-1 pb-1">
          <BeforeAfterSlider
            beforeSrc={item.previewUrl}
            afterSrc={afterUrl}
            height="h-[50vh] sm:h-[55vh]"
            fit="contain"
          />
        </div>
      </details>

      <div className="flex items-center justify-between gap-3 px-1 flex-wrap">
        {/* iter v32 r5 — User M1671: "genera caption" qui non serve, va dentro
            l'editor social. Rimosso da qui. La caption è disponibile via:
            (1) "Altre" → "Crea storia social" → Annuncio Lampo → Social Studio
            (2) Direttamente nel Social Studio di un annuncio. */}
        <div />
        <RegenerateButton onRegenerate={onRegenerate} disabled={disabled} kind="photo" testid="foto-regenerate" />
      </div>

      {/* iter v28 r358 — User M1267: bottone "Non ti piace? Rigenera" sempre
          visibile sotto il risultato. Ri-esegue con gli stessi parametri
          (ogni run è leggermente diverso perché il modello è stocastico).
          Per la modalità Migliora aggiungiamo anche un quick-toggle
          "Ultra-fedele" che forza focus=fedele al volo (zero invenzioni). */}
      <div
        className="rounded-2xl border border-[#EAE7DF] bg-[#F8F6F1] px-4 py-3 space-y-2.5"
        data-testid="foto-result-rigenera-box"
      >
        <div className="text-[12px] text-[#3F3D38] leading-snug">
          <span className="font-semibold">Non ti piace il risultato?</span>{" "}
          Pronto può rifare la stessa foto: ogni volta esce un po&apos; diverso.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onRigenera}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1C1C1A] text-white text-[12px] font-semibold hover:bg-[#2A2A28] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="foto-result-rigenera-btn"
          >
            <ArrowsClockwise size={12} weight="bold" /> Rigenera (stesse impostazioni)
          </button>
          {showUltraFedele && (
            <button
              type="button"
              onClick={onUltraFedele}
              disabled={disabled}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                ultraFedele
                  ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                  : "bg-white text-[#7C8B6E] border-[#7C8B6E] hover:bg-[#F1F4ED]"
              }`}
              data-testid="foto-result-ultrafedele-btn"
              title="Ri-esegue toccando solo luce ed esposizione. Zero invenzioni."
            >
              <ShieldCheck size={12} weight={ultraFedele ? "fill" : "bold"} />
              {ultraFedele ? "Rigenera · Ultra-fedele attivo" : "Rigenera in Ultra-fedele"}
            </button>
          )}
        </div>
        <div className="text-[10.5px] text-[#9A968B] leading-snug">
          {showUltraFedele
            ? "Ultra-fedele = solo correzioni di luce, niente cambi di mobili, finestre, porte."
            : "Ogni tentativo consuma 1 foto dal budget mensile."}
        </div>
      </div>
    </div>
  );
}
